// ============================================================================
// Autore: Salvo de Luca
// Azienda Dialogicom s.r.l.s.
// Nome file: EmailChangeSuccessPage.tsx
// Percorso: frontend/src/components/auth/EmailChangeSuccessPage.tsx
// Release: v1.0.0 - Pagina conferma cambio email
// Data: 20/01/2026
// ============================================================================

import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle, Mail, LogIn } from 'lucide-react';

const EmailChangeSuccessPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [newEmail, setNewEmail] = useState<string>('');
  const [countdown, setCountdown] = useState<number>(10);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const email = params.get('email');
    if (email) {
      setNewEmail(decodeURIComponent(email));
    }
  }, [location]);

  // Countdown per redirect automatico
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      navigate('/sign-in');
    }
  }, [countdown, navigate]);

  const handleGoToLogin = () => {
    navigate('/sign-in');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 text-white p-4">
      <div className="bg-slate-800 p-8 rounded-xl shadow-xl text-center max-w-md w-full">
        {/* Icona successo */}
        <div className="flex justify-center mb-6">
          <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center">
            <CheckCircle className="w-12 h-12 text-green-400" />
          </div>
        </div>

        {/* Titolo */}
        <h1 className="text-2xl font-bold mb-4 text-green-400">
          Email Cambiata con Successo!
        </h1>

        {/* Messaggio */}
        <p className="text-gray-300 mb-4">
          Il tuo indirizzo email è stato aggiornato correttamente.
        </p>

        {/* Nuova email */}
        {newEmail && (
          <div className="bg-slate-700/50 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-center gap-2 text-sm text-gray-400 mb-1">
              <Mail className="w-4 h-4" />
              <span>Nuova email:</span>
            </div>
            <p className="text-lg font-semibold text-white break-all">
              {newEmail}
            </p>
          </div>
        )}

        {/* Avviso */}
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mb-6">
          <p className="text-amber-300 text-sm">
            Per motivi di sicurezza, dovrai effettuare nuovamente l'accesso con la nuova email.
          </p>
        </div>

        {/* Pulsante login */}
        <button
          onClick={handleGoToLogin}
          className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-500 rounded-lg font-medium transition-colors"
        >
          <LogIn className="w-5 h-5" />
          Vai al Login
        </button>

        {/* Countdown */}
        <p className="mt-4 text-sm text-gray-500">
          Redirect automatico in {countdown} secondi...
        </p>
      </div>
    </div>
  );
};

export default EmailChangeSuccessPage;
